import { EmptyState } from '../components/ui/EmptyState/EmptyState';
import { AppLayout } from '../layout/AppLayout';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../hooks/redux';
import { Plus, UserGroup } from '../icons/SvgIcons';
import Loader from '../components/ui/Loader/Loader';
import { useAiSystemOverview } from '../components/hooks/useAiSystemOverview';
import Modal from '../components/ui/Modal/Modal';
import { InviteUser } from '../components/UserManagement/InviteUser';
import { useUserManagement } from '../components/hooks/useUserManagement';
import { UserOverview } from '../components/features/UserManagement/UserOverview/UserOverview';
import { useDispatch } from 'react-redux';
import { Toast } from '../components/ui/Toast/Toast';
import { useAccountDefault } from '../components/hooks/useAccountDefault';
import {
  fetchDeleteUserFromAccountSuccess,
  setCurrentUser,
  setIsUserInvited
} from '../store/slices/userManagementSlice';
import { RegisteredUserProfile } from './RegisteredUserProfile';
import { AssignUser } from '../components/UserManagement/AssignUser';
import { inviteUser } from '../services/TeamService';
import { getCurrentUser } from '../services/UserService';
import { featureFlags, appConfig } from '../config/featureFlags';
import {
  selectIsSuperAdmin,
  selectIsUserRole
} from '../store/selectors/authSelectors';

export function UserManagementPage() {
  const dispatch = useDispatch();
  const profileHistoryPushedRef = useRef(false);
  const [showToast, setShowToast] = useState(false);
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false);
  const [assignUserModalOpen, setAssignUserModalOpen] = useState(false);
  const [deletingMemberToast, setDeletingMemberToast] = useState(false);
  const [shgAdminInviteToast, setShgAdminInviteToast] = useState(false);
  const [isInitialDataLoading, setIsInitialDataLoading] = useState(true);
  const [invitingSHGAdmin, setInvitingSHGAdmin] = useState(false);

  const { total, loading } = useAppSelector((state) => state.aiSystems);

  const { account, accountLoading } = useAppSelector(
    (state) => state.accountDefault
  );

  const isSuperAdminRole = useAppSelector(selectIsSuperAdmin);
  const isUserRole = useAppSelector(selectIsUserRole);

  const isPrivateCloud = featureFlags.isPrivateCloud;
  const shgAdminEmail = appConfig.shgAdminEmail;

  const {
    teamInvitations,
    teamMembers,
    teamInvitationsLoading,
    teamMembersLoading,
    teamUsersLoading,
    isUserInvited,
    invitingUser,
    currentUser,
    deleteUserFromAccountSuccess,
    deleteUserFromAccountLoading,
    removedLastSystemPerUserSuccess,
    removedLastSystemPerUserLoading,
    teamRoles,
    teamRolesLoading,
    sorting
  } = useAppSelector((state) => state.userManagement);

  const { fetchSystems } = useAiSystemOverview();
  const {
    fetchTeamInvitations,
    fetchTeamMembers,
    fetchTeamRoles,
    fetchTeamUsers
  } = useUserManagement();
  const { fetchMyAccount } = useAccountDefault();

  const fetchTeamDataIfAllowed = useCallback(
    async (accountId: string): Promise<void> => {
      const requests = [
        fetchTeamInvitations(accountId),
        fetchTeamMembers(accountId)
      ];

      if (!isSuperAdminRole) {
        requests.push(fetchTeamUsers());
      }

      await Promise.all(requests);
    },
    [fetchTeamInvitations, fetchTeamMembers, fetchTeamUsers, isSuperAdminRole]
  );

  function handleAddUser() {
    setInviteUserModalOpen(true);
  }

  useEffect(() => {
    let isMounted = true;

    const loadInitialUserManagementData = async () => {
      setIsInitialDataLoading(true);
      setShowToast(false);
      setDeletingMemberToast(false);
      dispatch(setCurrentUser(null));

      try {
        const [currentAccount] = await Promise.all([
          fetchMyAccount(),
          fetchTeamRoles()
        ]);

        if (!isMounted || !currentAccount) {
          return;
        }

        const requests = [fetchTeamDataIfAllowed(currentAccount.id)];

        if (!isSuperAdminRole) {
          let currentUserId: string | undefined;

          if (isUserRole) {
            const currentUserResponse = await getCurrentUser();
            currentUserId = currentUserResponse.data.id;
          }

          requests.push(
            fetchSystems(false, currentUserId, true)
          );
        }

        await Promise.all(requests);
      } catch (error) {
        console.error('Failed to bootstrap user management:', error);
      } finally {
        if (isMounted) {
          setIsInitialDataLoading(false);
        }
      }
    };

    loadInitialUserManagementData();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (account && teamInvitations && teamMembers) {
      if (teamMembers?.count > 0) {
        fetchTeamMembers(account.id, sorting.sortOrder);
      }
    }
  }, [sorting]);

  useEffect(() => {
    if (isUserInvited && account) {
      fetchTeamDataIfAllowed(account.id);
      setShowToast(true);
      dispatch(setIsUserInvited(false));
    }
  }, [isUserInvited]);

  useEffect(() => {
    if (deleteUserFromAccountSuccess && account) {
      fetchTeamDataIfAllowed(account.id);
      setDeletingMemberToast(true);
      dispatch(fetchDeleteUserFromAccountSuccess(false));
    }
  }, [deleteUserFromAccountSuccess]);

  useEffect(() => {
    if (removedLastSystemPerUserSuccess && account) {
      fetchTeamDataIfAllowed(account.id);
    }
  }, [removedLastSystemPerUserSuccess]);

  useEffect(() => {
    if (!currentUser) {
      profileHistoryPushedRef.current = false;
      return;
    }

    if (!profileHistoryPushedRef.current) {
      window.history.pushState(
        { userManagementProfileOpen: true },
        '',
        window.location.href
      );
      profileHistoryPushedRef.current = true;
    }

    const handlePopState = () => {
      dispatch(setCurrentUser(null));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentUser, dispatch]);

  const hasSystems = total && total > 0;
  const hasTeamInvitations = (teamInvitations?.total ?? 0) > 0;
  const hasMembers = (teamMembers?.total ?? 0) > 0;
  const hasNoPlan = account?.subscription?.plan === 'NO_PLAN';
  const canInviteUser = !isUserRole;
  const canAssignUser = !isSuperAdminRole && hasSystems;

  const isUserManagementLoading =
    isInitialDataLoading ||
    loading ||
    accountLoading ||
    teamInvitationsLoading ||
    teamMembersLoading ||
    teamUsersLoading ||
    teamRolesLoading ||
    invitingUser ||
    deleteUserFromAccountLoading ||
    removedLastSystemPerUserLoading;

  const showEmptyState =
    !isUserManagementLoading &&
    !currentUser &&
    !hasTeamInvitations &&
    !hasMembers;

  const showUserOverview =
    !isUserManagementLoading &&
    !currentUser &&
    (hasTeamInvitations || hasMembers);

  const handleSeccondButton = async () => {
    // check super admin role and private cloud
    if (isSuperAdminRole && isPrivateCloud) {
      setInvitingSHGAdmin(true);
      try {
        await inviteUser({
          email: shgAdminEmail,
          role: 'ADMIN'
        });
        setShgAdminInviteToast(true);
      } catch (error) {
        console.error('Failed to invite SHG Admin:', error);
      } finally {
        setInvitingSHGAdmin(false);
      }
      return;
    }

    setAssignUserModalOpen(true);
  };

  // check super admin and private cloud
  const seccondButtonText =
    isSuperAdminRole && isPrivateCloud
      ? 'Invite SHG Admin'
      : canAssignUser
        ? 'Assign user'
        : undefined;

  const secconButtonDisabled = isSuperAdminRole ? false : !canAssignUser;

  return (
    <AppLayout>
      <DashboardLayout
        title="User Management"
        addButtonText={canInviteUser ? 'Invite User' : undefined}
        onAddButtonClick={handleAddUser}
        addButtonDisabled={!canInviteUser}
        secondButtonText={seccondButtonText}
        secondButtonOnClick={handleSeccondButton}
        secondButtonDisabled={secconButtonDisabled || invitingSHGAdmin}
      >
        {isUserManagementLoading && (
          <div className="absolute top-1/2 left-1/2">
            <Loader />
          </div>
        )}

        {currentUser != null && <RegisteredUserProfile />}

        {showEmptyState && (
          <EmptyState
            icon={<UserGroup />}
            title={'No Users yet'}
            description={
              canInviteUser
                ? 'Add your first user to begin managing access.'
                : hasNoPlan
                  ? 'User management is unavailable without an active subscription plan.'
                  : 'User management is unavailable until at least one AI system exists.'
            }
            buttonIcon={canInviteUser ? <Plus /> : undefined}
            buttonText={canInviteUser ? 'Invite User' : undefined}
            onButtonClick={canInviteUser ? handleAddUser : undefined}
          />
        )}
        {showUserOverview && <UserOverview />}

        {showToast && (
          <Toast
            text="User successfully invited!"
            showToast={showToast}
            setShowToast={setShowToast}
          />
        )}
        {deletingMemberToast && (
          <Toast
            text="User successfully deleted!"
            showToast={deletingMemberToast}
            setShowToast={setDeletingMemberToast}
          />
        )}
        {shgAdminInviteToast && (
          <Toast
            text="SHG Admin successfully invited!"
            showToast={shgAdminInviteToast}
            setShowToast={setShgAdminInviteToast}
          />
        )}

        <Modal
          open={inviteUserModalOpen}
          setOpen={setInviteUserModalOpen}
          dialogPanelClassnames="min-w-[820px] max-h-[calc(100vh_-_70px)] overflow-hidden"
        >
          <InviteUser
            setIsOpen={setInviteUserModalOpen}
            userRoles={teamRoles?.platformRoles || []}
            showCompanyInput={isSuperAdminRole && !isPrivateCloud}
          />
        </Modal>
        <Modal
          open={assignUserModalOpen}
          setOpen={setAssignUserModalOpen}
          dialogPanelClassnames="min-w-[820px] max-h-[calc(100vh_-_70px)] overflow-hidden"
        >
          <AssignUser
            setIsOpen={setAssignUserModalOpen}
            userRoles={teamRoles?.aiSystemRoles || []}
          />
        </Modal>
      </DashboardLayout>
    </AppLayout>
  );
}
